import { useEffect, useState } from "react";
import { X, Save, Loader2 } from "lucide-react";

interface Props<T extends { id: number; internalNotes: string | null }> {
  open: boolean;
  data: T | null;
  loading?: boolean;
  /** Bloques de información read-only del detalle (titulo + contenido JSX) */
  renderBody: (data: T) => React.ReactNode;
  /** Slot para botones de acción (approve/reject/verify/etc.) */
  renderActions?: (data: T) => React.ReactNode;
  title: string;
  subtitle?: string;
  onClose: () => void;
  onSaveNotes: (notes: string | null) => Promise<void>;
}

export function ApplicationDrawer<T extends { id: number; internalNotes: string | null }>({
  open,
  data,
  loading,
  renderBody,
  renderActions,
  title,
  subtitle,
  onClose,
  onSaveNotes,
}: Props<T>) {
  const [notes, setNotes] = useState<string>("");
  const [savingNotes, setSavingNotes] = useState(false);

  // Sincronizar notas cuando cambia el row seleccionado
  useEffect(() => {
    setNotes(data?.internalNotes ?? "");
  }, [data?.id, data?.internalNotes]);

  // Cerrar con Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const handleSave = async () => {
    setSavingNotes(true);
    try {
      await onSaveNotes(notes.trim() || null);
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 z-40 animate-fade-in"
        onClick={onClose}
        aria-hidden
      />
      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[520px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="nx-h2 truncate">{title}</h2>
            {subtitle && (
              <p className="text-2xs text-fg-subtle mt-0.5">{subtitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="nx-btn-ghost p-1.5"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading || !data ? (
            <div className="flex items-center justify-center py-12 text-fg-subtle">
              <Loader2 size={16} className="animate-spin mr-2" />
              Cargando detalle…
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-5">{renderBody(data)}</div>

              <div className="mt-6">
                <label className="nx-eyebrow block mb-2">Notas internas</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="nx-input resize-y"
                  placeholder="Notas privadas del equipo (no visibles al postulante)…"
                />
                <button
                  onClick={handleSave}
                  disabled={savingNotes || notes === (data.internalNotes ?? "")}
                  className="nx-btn-secondary mt-2"
                >
                  {savingNotes ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Save size={14} />
                  )}
                  Guardar notas
                </button>
              </div>
            </>
          )}
        </div>

        {data && renderActions && (
          <footer className="px-5 py-3 border-t border-border bg-bg-surface flex flex-wrap items-center gap-2 justify-end">
            {renderActions(data)}
          </footer>
        )}
      </aside>
    </>
  );
}
